import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import type { IMeetingEmailTemplate } from '../Interfaces/IMeetingEmailTemplate';
import type { ILegalTransactionMeetingEmail } from '../Interfaces/ILegalTransactionMeetingEmail';
import type { ILegalTransaction } from '../Interfaces/ILegalTransaction';
import { useGetMeetingEmailTemplates } from '../hooks/reactQuerys/MeetingEmailTemplateQueries';
import {
  useGetLegalTransactionFullObject,
  useGetLegalTransactionTypeFullObject,
  useGetLegalTransactionTypes,
  useGetLegalTransactions,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import {
  copyMeetingEmailsToMeetingEmails,
  copyTemplatesToMeetingEmails,
  copyTemplatesToTemplates,
} from './importUtils';

type ImportMode = 'template' | 'meeting';
type ImportSource = 'global' | 'legalTransactionType' | 'legalTransaction';

interface Props {
  open: boolean;
  onClose: () => void;
  mode: ImportMode;
  idLegalTransactionType?: number | null;
  idLegalTransaction?: number;
  idLegalTransactionMeeting?: number;
  existingTemplates?: IMeetingEmailTemplate[];
  existingMeetingEmails?: ILegalTransactionMeetingEmail[];
  onImportTemplates?: (items: IMeetingEmailTemplate[]) => void;
  onImportMeetingEmails?: (items: ILegalTransactionMeetingEmail[]) => void;
}

const SOURCE_OPTIONS: Record<
  ImportMode,
  Array<{ value: ImportSource; label: string }>
> = {
  template: [
    { value: 'global', label: 'Globale Vorlagen' },
    { value: 'legalTransactionType', label: 'Andere Rechtsgeschäftsart' },
  ],
  meeting: [
    { value: 'legalTransactionType', label: 'Rechtsgeschäftsart' },
    { value: 'global', label: 'Globale Vorlagen' },
  ],
};

const TEMPLATE_COLUMNS = [
  { key: 'Title', label: 'Titel', width: '28%' },
  { key: 'Subject', label: 'Betreff', width: '28%' },
];

const EVENT_COLUMNS = [
  { key: 'sendOnCreation', label: 'Terminerst.', width: '9%' },
  { key: 'sendOnReschedule', label: 'Terminänd.', width: '9%' },
  { key: 'sendOnCancelling', label: 'Terminabs.', width: '9%' },
  { key: 'sendWithCalenderFile', label: 'Kalender', width: '9%' },
];

const matchesSearch = (title: string, subject: string, searchTerm: string) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return (
    title.toLowerCase().includes(normalizedSearch) ||
    subject.toLowerCase().includes(normalizedSearch)
  );
};

const getTemplateKey = (template: IMeetingEmailTemplate) =>
  `template-${template.idMeetingEmailTemplate}`;

const getMeetingEmailKey = (meetingEmail: ILegalTransactionMeetingEmail) =>
  `meeting-${meetingEmail.idLegalTransactionMeetingEmail}`;

export const GenericMeetingEmailImport: React.FC<Props> = ({
  open,
  onClose,
  mode,
  idLegalTransactionType = null,
  idLegalTransaction = -1,
  idLegalTransactionMeeting = -1,
  existingTemplates = [],
  existingMeetingEmails = [],
  onImportTemplates,
  onImportMeetingEmails,
}) => {
  const sourceOptions = SOURCE_OPTIONS[mode];
  const defaultSource =
    mode === 'meeting' ? ('legalTransactionType' as ImportSource) : sourceOptions[0].value;
  const [source, setSource] = useState<ImportSource>(defaultSource);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLegalTransactionTypeId, setSelectedLegalTransactionTypeId] =
    useState<number | null>(null);
  const [selectedLegalTransaction, setSelectedLegalTransaction] =
    useState<ILegalTransaction | null>(null);
  const [selectedSourceMeetingId, setSelectedSourceMeetingId] = useState<
    number | null
  >(null);

  const { meetingEmailTemplates, isLoadingMeetingEmailTemplates } =
    useGetMeetingEmailTemplates();
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();
  const { legalTransactions, isLoadingLegalTransactions } =
    useGetLegalTransactions(mode === 'meeting' && source === 'legalTransaction');
  const {
    legalTransactionTypeFullObject,
    isLoadingLegalTransactionTypeFullObject,
  } = useGetLegalTransactionTypeFullObject(selectedLegalTransactionTypeId ?? 0);
  const {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
  } = useGetLegalTransactionFullObject(
    selectedLegalTransaction?.idLegalTransaction
  );

  useEffect(() => {
    if (open) {
      setSource(defaultSource);
      setSelectedIds([]);
      setSearchTerm('');
      setSelectedLegalTransactionTypeId(
        defaultSource === 'legalTransactionType' ? idLegalTransactionType : null
      );
      setSelectedLegalTransaction(null);
      setSelectedSourceMeetingId(null);
    }
  }, [defaultSource, idLegalTransactionType, open]);

  useEffect(() => {
    setSelectedIds([]);
    setSearchTerm('');
    setSelectedLegalTransactionTypeId(
      source === 'legalTransactionType' ? idLegalTransactionType : null
    );
    setSelectedLegalTransaction(null);
    setSelectedSourceMeetingId(null);
  }, [idLegalTransactionType, source]);

  const availableTransactionTypes = useMemo(
    () =>
      (legalTransactionTypes ?? []).filter(
        transactionType =>
          idLegalTransactionType === null ||
          transactionType.idLegalTransactionType !== idLegalTransactionType
      ),
    [legalTransactionTypes, idLegalTransactionType]
  );

  const sourceTemplates = useMemo(() => {
    if (!meetingEmailTemplates) return [];

    if (source === 'global') {
      return meetingEmailTemplates.filter(
        template => template.idLegalTransactionType === null
      );
    }

    if (
      source === 'legalTransactionType' &&
      legalTransactionTypeFullObject?.MeetingEmailTemplateArray
    ) {
      return legalTransactionTypeFullObject.MeetingEmailTemplateArray;
    }

    return [];
  }, [meetingEmailTemplates, source, legalTransactionTypeFullObject]);

  const availableSourceMeetings =
    legalTransactionFullObject?.LegalTransactionMeetingArray ?? [];

  const sourceMeetingEmails = useMemo(() => {
    if (
      source !== 'legalTransaction' ||
      !legalTransactionFullObject ||
      selectedSourceMeetingId === null
    ) {
      return [];
    }

    return legalTransactionFullObject.LegalTransactionMeetingEmailArray.filter(
      meetingEmail =>
        meetingEmail.idLegalTransactionMeeting === selectedSourceMeetingId
    );
  }, [source, legalTransactionFullObject, selectedSourceMeetingId]);

  const displayedTemplates = useMemo(
    () =>
      sourceTemplates.filter(template =>
        matchesSearch(template.Title, template.Subject, searchTerm)
      ),
    [sourceTemplates, searchTerm]
  );

  const displayedMeetingEmails = useMemo(
    () =>
      sourceMeetingEmails.filter(meetingEmail =>
        matchesSearch(meetingEmail.Title, meetingEmail.Subject, searchTerm)
      ),
    [sourceMeetingEmails, searchTerm]
  );

  const displayedIds = useMemo(
    () =>
      mode === 'meeting' && source === 'legalTransaction'
        ? displayedMeetingEmails.map(getMeetingEmailKey)
        : displayedTemplates.map(getTemplateKey),
    [displayedMeetingEmails, displayedTemplates, mode, source]
  );

  const isLoading =
    isLoadingMeetingEmailTemplates ||
    isLoadingLegalTransactionTypes ||
    isLoadingLegalTransactions ||
    (source === 'legalTransactionType' &&
      selectedLegalTransactionTypeId !== null &&
      isLoadingLegalTransactionTypeFullObject) ||
    (source === 'legalTransaction' &&
      selectedLegalTransaction !== null &&
      isFetchingLegalTransactionFullObject);

  const toggle = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? displayedIds : []);
  };

  const handleImport = () => {
    if (selectedIds.length === 0) return;

    if (mode === 'template') {
      const selectedTemplates = sourceTemplates.filter(template =>
        selectedIds.includes(getTemplateKey(template))
      );

      onImportTemplates?.(
        copyTemplatesToTemplates({
          templates: selectedTemplates,
          existingTemplates,
          idLegalTransactionType,
        })
      );
      onClose();
      return;
    }

    if (source === 'legalTransaction') {
      const selectedMeetingEmails = sourceMeetingEmails.filter(meetingEmail =>
        selectedIds.includes(getMeetingEmailKey(meetingEmail))
      );

      onImportMeetingEmails?.(
        copyMeetingEmailsToMeetingEmails({
          meetingEmails: selectedMeetingEmails,
          existingMeetingEmails,
          idLegalTransactionMeeting,
          idLegalTransaction,
        })
      );
      onClose();
      return;
    }

    const selectedTemplates = sourceTemplates.filter(template =>
      selectedIds.includes(getTemplateKey(template))
    );

    onImportMeetingEmails?.(
      copyTemplatesToMeetingEmails({
        templates: selectedTemplates,
        existingMeetingEmails,
        idLegalTransactionMeeting,
        idLegalTransaction,
      })
    );
    onClose();
  };

  const renderRows = () => {
    if (mode === 'meeting' && source === 'legalTransaction') {
      if (
        source === 'legalTransaction' &&
        selectedLegalTransaction &&
        !selectedSourceMeetingId
      ) {
        return (
          <Typography>
            Bitte wählen Sie zunächst den Quell-Termin aus.
          </Typography>
        );
      }

      if (displayedMeetingEmails.length === 0) {
        return <Typography>Keine passenden Termin-E-Mails gefunden.</Typography>;
      }

      return (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' sx={{ width: '6%' }}>
                <Checkbox
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < displayedIds.length
                  }
                  checked={
                    displayedIds.length > 0 &&
                    selectedIds.length === displayedIds.length
                  }
                  onChange={event => handleSelectAll(event.target.checked)}
                />
              </TableCell>
              {TEMPLATE_COLUMNS.map(column => (
                <TableCell key={column.key} sx={{ width: column.width }}>
                  {column.label}
                </TableCell>
              ))}
              {EVENT_COLUMNS.map(column => (
                <TableCell key={column.key} sx={{ width: column.width }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedMeetingEmails.map(meetingEmail => (
              <TableRow
                key={meetingEmail.idLegalTransactionMeetingEmail}
                hover
              >
                <TableCell padding='checkbox'>
                  <Checkbox
                    checked={selectedIds.includes(getMeetingEmailKey(meetingEmail))}
                    onChange={() => toggle(getMeetingEmailKey(meetingEmail))}
                  />
                </TableCell>
                <TableCell>{meetingEmail.Title}</TableCell>
                <TableCell>{meetingEmail.Subject}</TableCell>
                <TableCell>
                  {Boolean(meetingEmail.sendOnCreation) ? <Check /> : <Close />}
                </TableCell>
                <TableCell>
                  {Boolean(meetingEmail.sendOnReschedule) ? (
                    <Check />
                  ) : (
                    <Close />
                  )}
                </TableCell>
                <TableCell>
                  {Boolean(meetingEmail.sendOnCancelling) ? (
                    <Check />
                  ) : (
                    <Close />
                  )}
                </TableCell>
                <TableCell>
                  {Boolean(meetingEmail.sendWithCalenderFile) ? (
                    <Check />
                  ) : (
                    <Close />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    if (displayedTemplates.length === 0) {
      return <Typography>Keine passenden Vorlagen gefunden.</Typography>;
    }

    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox' sx={{ width: '6%' }}>
              <Checkbox
                indeterminate={
                  selectedIds.length > 0 &&
                  selectedIds.length < displayedIds.length
                }
                checked={
                  displayedIds.length > 0 &&
                  selectedIds.length === displayedIds.length
                }
                onChange={event => handleSelectAll(event.target.checked)}
              />
            </TableCell>
            {TEMPLATE_COLUMNS.map(column => (
              <TableCell key={column.key} sx={{ width: column.width }}>
                {column.label}
              </TableCell>
            ))}
            {EVENT_COLUMNS.map(column => (
              <TableCell key={column.key} sx={{ width: column.width }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedTemplates.map(template => (
            <TableRow key={template.idMeetingEmailTemplate} hover>
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={selectedIds.includes(getTemplateKey(template))}
                  onChange={() => toggle(getTemplateKey(template))}
                />
              </TableCell>
              <TableCell>{template.Title}</TableCell>
              <TableCell>{template.Subject}</TableCell>
              <TableCell>
                {Boolean(template.sendOnCreation) ? <Check /> : <Close />}
              </TableCell>
              <TableCell>
                {Boolean(template.sendOnReschedule) ? <Check /> : <Close />}
              </TableCell>
              <TableCell>
                {Boolean(template.sendOnCancelling) ? <Check /> : <Close />}
              </TableCell>
              <TableCell>
                {Boolean(template.sendWithCalenderFile) ? (
                  <Check />
                ) : (
                  <Close />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
      <DialogTitle>
        {mode === 'template'
          ? 'Termin-E-Mail-Vorlagen importieren'
          : 'Termin-E-Mails importieren'}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display='flex' justifyContent='center' my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              mb={2}
              display='grid'
              gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
              gap={2}
            >
              <TextField
                select
                label='Quelle'
                size='small'
                value={source}
                onChange={event =>
                  setSource(event.target.value as ImportSource)
                }
              >
                {sourceOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {source === 'legalTransactionType' && (
                <Autocomplete
                  options={availableTransactionTypes}
                  value={
                    availableTransactionTypes.find(
                      transactionType =>
                        transactionType.idLegalTransactionType ===
                        selectedLegalTransactionTypeId
                    ) ?? null
                  }
                  getOptionLabel={option => option.LegalTransactionType}
                  isOptionEqualToValue={(option, value) =>
                    option.idLegalTransactionType ===
                    value.idLegalTransactionType
                  }
                  onChange={(_, value) =>
                    setSelectedLegalTransactionTypeId(
                      value?.idLegalTransactionType ?? null
                    )
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Rechtsgeschäftsart'
                      size='small'
                    />
                  )}
                />
              )}

              {source === 'legalTransaction' && (
                <Autocomplete
                  options={legalTransactions ?? []}
                  value={selectedLegalTransaction}
                  getOptionLabel={option =>
                    option.Title?.trim()
                      ? option.Title
                      : `${option.LegalTransactionType ?? 'Vorgang'} #${option.idLegalTransaction}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.idLegalTransaction === value.idLegalTransaction
                  }
                  onChange={(_, value) => {
                    setSelectedLegalTransaction(value);
                    setSelectedSourceMeetingId(null);
                  }}
                  renderInput={params => (
                    <TextField {...params} label='Vorgang' size='small' />
                  )}
                />
              )}

              {source === 'legalTransaction' && (
                <Autocomplete
                  options={availableSourceMeetings}
                  value={
                    availableSourceMeetings.find(
                      meeting =>
                        meeting.idLegalTransactionMeeting ===
                        selectedSourceMeetingId
                    ) ?? null
                  }
                  getOptionLabel={option =>
                    option.LegalTransactionMeeting ||
                    option.Meeting_at ||
                    `Termin #${option.idLegalTransactionMeeting}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.idLegalTransactionMeeting ===
                    value.idLegalTransactionMeeting
                  }
                  onChange={(_, value) =>
                    setSelectedSourceMeetingId(
                      value?.idLegalTransactionMeeting ?? null
                    )
                  }
                  renderInput={params => (
                    <TextField {...params} label='Quell-Termin' size='small' />
                  )}
                  disabled={!selectedLegalTransaction}
                />
              )}
            </Box>

            <Box mb={2}>
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                label='Suchen nach Titel oder Betreff'
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
              />
            </Box>

            {renderRows()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleImport}
          disabled={selectedIds.length === 0}
          variant='contained'
        >
          Importieren ({selectedIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
